<script setup>
import {onMounted, ref, computed} from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: '',
    },
    type: {
        type: String,
        default: 'text',
    },
    placeholder: {
        type: String,
        default: '',
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    readOnly: {
        type: Boolean,
        default: false,
    },
    required: {
        type: Boolean,
        default: false,
    },
    autocomplete: {
        type: String,
        default: '',
    },
    id: {
        type: String,
        default: '',
    },
    name: {
        type: String,
        default: '',
    },
    maxlength: {
        type: [Number, String],
        default: null,
    },
    min: {
        type: [Number, String],
        default: null,
    },
    max: {
        type: [Number, String],
        default: null,
    },
    step: {
        type: [Number, String],
        default: null,
    },
    error: {
        type: Boolean,
        default: false,
    },
    showPasswordToggle: {
        type: Boolean,
        default: false,
    }
});

const emit = defineEmits(['update:modelValue', 'blur', 'focus', 'keydown', 'keyup', 'keypress']);

const input = ref(null);
const passwordVisible = ref(false);

const inputType = computed(() => {
    if (props.type === 'password' && passwordVisible.value) {
        return 'text';
    }
    return props.type;
});

const togglePassword = () => {
    passwordVisible.value = !passwordVisible.value;
};

const inputClasses = computed(() => {
    return [
        'border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300 focus:border-indigo-500 dark:focus:border-indigo-600 focus:ring-indigo-500 dark:focus:ring-indigo-600 rounded-md shadow-sm w-full',
        props.disabled ? 'bg-gray-50 text-gray-700 cursor-not-allowed' : '',
        props.error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : '',
    ];
});

onMounted(() => {
    if (input.value?.hasAttribute('autofocus')) {
        input.value.focus();
    }
});

defineExpose({
    focus: () => input.value?.focus(),
    blur: () => input.value?.blur(),
    input: input,
});
</script>

<template>
    <div class="relative">
        <input
            ref="input"
            :value="modelValue"
            :type="inputType"
            :class="inputClasses"
            :disabled="disabled"
            :readonly="readOnly"
            :required="required"
            :placeholder="placeholder"
            :autocomplete="autocomplete"
            :id="id"
            :name="name"
            :maxlength="maxlength"
            :min="min"
            :max="max"
            :step="step"
            @input="$emit('update:modelValue', $event.target.value)"
            @blur="$emit('blur', $event)"
            @focus="$emit('focus', $event)"
            @keydown="$emit('keydown', $event)"
            @keyup="$emit('keyup', $event)"
            @keypress="$emit('keypress', $event)"
        />

        <!-- Password toggle button -->
        <button
            v-if="type === 'password' && showPasswordToggle"
            type="button"
            class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600"
            @click.prevent="togglePassword"
        >
            <svg
                v-if="passwordVisible"
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                />
            </svg>
            <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                class="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
            </svg>
        </button>
    </div>
</template>
